import BuyShoppingCartIcon from "@/assets/icons/Buy shopping cart.svg?react";
import { usePlayTrack } from "@/components/providers/AudioPlayerProvider/useAudioPlayer";
import HeartButton from "@/components/ui/HeartButton";
import TrackArtwork from "@/components/ui/TrackArtwork";
import type { ListTrackDto } from "@/lib/lutely-api/gen/dtos/listTrackDto";
import { getSoundkitClipPath } from "@/lib/soundkit-clip-path";
import { cn, formatDuration, hexToRgba } from "@/lib/utils";
import { TrackAlbumsLink } from "@/components/links/TrackAlbumsLink";
import { Flex, Text } from "@radix-ui/themes";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { FaPlay } from "react-icons/fa";
import { RiPauseFill } from "react-icons/ri";
import { CreatorLink } from "../links/CreatorLink";

export default function TrackRow({
  track,
  className,
  artistColor,
  disableHoverBg,
}: {
  track: ListTrackDto;
  className?: string;
  artistColor?: string;
  disableHoverBg?: boolean;
}) {
  const [isHovered, setIsHovered] = useState(false);
  const { isPlaying, currentTrackId, playTrack } = usePlayTrack(track);

  const isSoundkit = track.type === "SOUNDKIT";
  const hasPlayablePreview = Boolean(track.fileUrl);
  const isCurrentTrack = currentTrackId === track.trackId;
  const showOverlay = hasPlayablePreview && (isHovered || isCurrentTrack);
  const title = track.title || "Untitled";
  const titleClassName =
    "truncate font-bold text-[length:var(--font-size-3)] leading-[var(--line-height-3)]";

  return (
    <Flex
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={cn(
        "relative items-center",
        "h-[68px]",
        !disableHoverBg && !artistColor && "hover:bg-slate-100",
        className,
      )}
      style={
        !disableHoverBg && isHovered && artistColor
          ? { backgroundColor: hexToRgba(artistColor, 0.1) }
          : undefined
      }
    >
      {/*
        The artwork is the play control — a single button, so there is no
        interactive element nested inside another. The overlay is decoration.
      */}
      <button
        type="button"
        onClick={playTrack}
        disabled={!hasPlayablePreview}
        aria-label={isPlaying ? `Pause ${title}` : `Play ${title}`}
        className={cn(
          "w-[52px] h-[52px] mr-[15px] relative shrink-0",
          !isSoundkit && "rounded-[4px] overflow-hidden",
          hasPlayablePreview && "cursor-pointer",
        )}
      >
        <TrackArtwork
          type={track.type}
          src={track.imageUrl || "/placeholders/album_art_placeholder.png"}
          alt={`${title} cover`}
          className={cn("h-full w-full", !isSoundkit && "rounded-[4px]")}
        />

        {showOverlay && (
          <span
            className={cn(
              "absolute inset-0 flex items-center justify-center bg-[rgba(0,0,0,0.3)]",
              !isSoundkit && "rounded-[4px]",
            )}
            style={isSoundkit ? { clipPath: getSoundkitClipPath() } : undefined}
            aria-hidden="true"
          >
            <span
              className="flex h-[32px] w-[32px] items-center justify-center rounded-full"
              style={{
                backgroundColor: artistColor ?? "var(--accent-9)",
                color: artistColor ? "white" : "var(--accent-contrast)",
              }}
            >
              {isPlaying ? (
                <RiPauseFill className="text-[24px]" />
              ) : (
                <FaPlay className="text-[14px]" />
              )}
            </span>
          </span>
        )}
      </button>

      <Flex direction="column" className="flex-1 min-w-0 gap-[3px]">
        {/*
          Only someone who can edit the track gets a link to its page; everyone
          else lands on the album it sells from.
        */}
        {track.canEdit ? (
          <Link
            to="/tracks/$TRACK_ID"
            params={{ TRACK_ID: track.trackId }}
            className={cn(titleClassName, "hover:underline")}
          >
            {title}
          </Link>
        ) : (
          <TrackAlbumsLink
            albums={track.albums}
            label={`View ${title}`}
            className={cn(
              titleClassName,
              track.albums.length > 0 && "hover:underline",
            )}
          >
            {title}
          </TrackAlbumsLink>
        )}
        <CreatorLink creator={track.creator}>
          {track.creator.stageName}
        </CreatorLink>
      </Flex>

      {/* Siblings of the title link, never inside it. */}
      <Flex className="items-center gap-3">
        <HeartButton
          kind="track"
          id={track.trackId}
          iconSize={20}
          className="text-slate-600 hover:text-slate-900 hover:bg-slate-200"
        />
        {track.albums.length > 0 && (
          <TrackAlbumsLink
            albums={track.albums}
            label={`Buy ${title}`}
            className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full text-slate-600 hover:text-slate-900 hover:bg-slate-200"
          >
            <BuyShoppingCartIcon width={20} height={20} aria-hidden="true" />
          </TrackAlbumsLink>
        )}
        <Text size="2" className="font-semibold w-[45px] text-right">
          {formatDuration(Number(track.duration))}
        </Text>
      </Flex>
    </Flex>
  );
}
